import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Building2,
  ImageIcon,
  Phone,
  MapPin,
  Share2,
  ShieldCheck,
  PanelBottom,
  Save,
} from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminSection } from "@/components/admin/AdminSection";
import { MediaField } from "@/components/admin/MediaField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { getAdminWebsiteSettings, saveWebsiteSettings } from "@/lib/admin/cms.functions";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";

export const Route = createFileRoute("/_admin/admin/cms/settings")({
  component: WebsiteSettingsPage,
});

type Form = Record<string, string | boolean>;

const TEXT_FIELDS = [
  "company_name","legal_company_name","tagline","logo_url","favicon_url","footer_logo_url",
  "support_email","sales_email","support_phone","whatsapp_number","customer_care_numbers",
  "office_address","google_map_link","working_hours","facebook_url","instagram_url",
  "linkedin_url","youtube_url","twitter_url","gst_number","cin_number","fssai_number",
  "copyright_text","footer_description","footer_disclaimer","designed_by","made_in_text",
  "maintenance_message","android_app_link","ios_app_link",
] as const;

const BOOL_FIELDS = [
  "show_newsletter","show_payment_icons","show_social","show_compliance",
  "show_privacy_preferences","maintenance_mode",
] as const;

function Field({
  label,
  value,
  onChange,
  hint,
  textarea = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  textarea?: boolean;
}) {
  const id = label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs">{label}</Label>
      {textarea ? (
        <Textarea id={id} value={value} onChange={(e) => onChange(e.target.value)} rows={3} />
      ) : (
        <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} />
      )}
      {hint ? <p className="text-[11px] text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  hint?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-md border p-3">
      <div>
        <p className="text-sm font-medium text-foreground">{label}</p>
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function WebsiteSettingsPage() {
  const load = useServerFn(getAdminWebsiteSettings);
  const save = useServerFn(saveWebsiteSettings);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["cms-admin", "settings"], queryFn: () => load() });
  const [form, setForm] = useState<Form>({});
  const [loadedForm, setLoadedForm] = useState<Form>({});

  useEffect(() => {
    if (!data) return;
    const next: Form = {};
    for (const k of TEXT_FIELDS) next[k] = (data[k] as string | null) ?? "";
    for (const k of BOOL_FIELDS) next[k] = (data[k] as boolean | null) ?? (k === "maintenance_mode" ? false : true);
    setForm(next);
    setLoadedForm(next);
  }, [data]);

  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(loadedForm),
    [form, loadedForm],
  );

  useUnsavedChanges(dirty);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {};
      for (const k of TEXT_FIELDS) payload[k] = ((form[k] as string) ?? "").trim() || null;
      for (const k of BOOL_FIELDS) payload[k] = Boolean(form[k]);
      return save({ data: payload as never });
    },
    onSuccess: () => {
      toast.success("Website settings saved");
      setLoadedForm(form);
      void qc.invalidateQueries({ queryKey: ["cms-admin", "settings"] });
      void qc.invalidateQueries({ queryKey: ["cms"] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const t = (k: string) => (form[k] as string) ?? "";
  const set = (k: string) => (v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading settings…</p>;
  }

  return (
    <div className="space-y-4 pb-24">
      <AdminPageHeader
        title="Website Settings"
        description="Global business details used across the public website, footer and SEO."
      />

      <AdminSection number={1} icon={Building2} title="Business identity" description="Names and tagline shown across the site.">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Business Name" value={t("company_name")} onChange={set("company_name")} />
          <Field label="Legal Company Name" value={t("legal_company_name")} onChange={set("legal_company_name")} />
          <Field label="Tagline" value={t("tagline")} onChange={set("tagline")} />
        </div>
      </AdminSection>

      <AdminSection number={2} icon={ImageIcon} title="Logo & favicon" description="Pick assets from the shared Media Library.">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Logo</Label>
            <MediaField value={t("logo_url")} onChange={set("logo_url")} allowUrlInput />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Favicon</Label>
            <MediaField value={t("favicon_url")} onChange={set("favicon_url")} allowUrlInput />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Footer logo</Label>
            <MediaField value={t("footer_logo_url")} onChange={set("footer_logo_url")} allowUrlInput />
          </div>
        </div>
      </AdminSection>

      <AdminSection number={3} icon={Phone} title="Contact details" description="Emails, phone numbers and working hours.">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Support Email" value={t("support_email")} onChange={set("support_email")} />
          <Field label="Sales Email" value={t("sales_email")} onChange={set("sales_email")} />
          <Field label="Support Phone" value={t("support_phone")} onChange={set("support_phone")} />
          <Field label="WhatsApp Number" value={t("whatsapp_number")} onChange={set("whatsapp_number")} />
          <Field label="Customer Care Numbers" value={t("customer_care_numbers")} onChange={set("customer_care_numbers")} />
          <Field label="Working Hours" value={t("working_hours")} onChange={set("working_hours")} />
        </div>
      </AdminSection>

      <AdminSection number={4} icon={MapPin} title="Address & map" description="Corporate address and Google Maps embed link.">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Corporate Address" value={t("office_address")} onChange={set("office_address")} textarea />
          <Field label="Google Maps Embed" value={t("google_map_link")} onChange={set("google_map_link")} hint="Paste the Google Maps embed or share URL." />
        </div>
      </AdminSection>

      <AdminSection number={5} icon={Share2} title="Social media" description="Only https:// links are shown on the site.">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Facebook" value={t("facebook_url")} onChange={set("facebook_url")} />
          <Field label="Instagram" value={t("instagram_url")} onChange={set("instagram_url")} />
          <Field label="LinkedIn" value={t("linkedin_url")} onChange={set("linkedin_url")} />
          <Field label="YouTube" value={t("youtube_url")} onChange={set("youtube_url")} />
          <Field label="Twitter / X" value={t("twitter_url")} onChange={set("twitter_url")} />
        </div>
      </AdminSection>

      <AdminSection number={6} icon={ShieldCheck} title="Compliance" description="Statutory numbers displayed in the footer.">
        <div className="grid gap-4 md:grid-cols-3">
          <Field label="GST Number" value={t("gst_number")} onChange={set("gst_number")} />
          <Field label="FSSAI Number" value={t("fssai_number")} onChange={set("fssai_number")} />
          <Field label="CIN Number" value={t("cin_number")} onChange={set("cin_number")} />
        </div>
      </AdminSection>

      <AdminSection number={7} icon={PanelBottom} title="Footer text & sections" description="Bottom-bar copy and which footer blocks are shown.">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Copyright Text" value={t("copyright_text")} onChange={set("copyright_text")} />
          <Field label="Designed By" value={t("designed_by")} onChange={set("designed_by")} />
          <Field label="Made in Mumbai text" value={t("made_in_text")} onChange={set("made_in_text")} />
          <Field label="Footer Description" value={t("footer_description")} onChange={set("footer_description")} textarea />
          <Field label="Footer Disclaimer" value={t("footer_disclaimer")} onChange={set("footer_disclaimer")} textarea />
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <Toggle label="Newsletter block" checked={Boolean(form["show_newsletter"])} onChange={set("show_newsletter")} />
          <Toggle label="Payment icons" checked={Boolean(form["show_payment_icons"])} onChange={set("show_payment_icons")} />
          <Toggle label="Social media icons" checked={Boolean(form["show_social"])} onChange={set("show_social")} />
          <Toggle label="FSSAI & GST line" checked={Boolean(form["show_compliance"])} onChange={set("show_compliance")} />
          <Toggle label="Privacy preferences link" checked={Boolean(form["show_privacy_preferences"])} onChange={set("show_privacy_preferences")} />
          <Toggle
            label="Maintenance mode"
            checked={Boolean(form["maintenance_mode"])}
            onChange={set("maintenance_mode")}
            hint="Shows the maintenance message instead of the site."
          />
        </div>
        <div className="mt-4">
          <Field label="Maintenance Message" value={t("maintenance_message")} onChange={set("maintenance_message")} textarea />
        </div>
      </AdminSection>

      <div className="sticky bottom-4 flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => setForm(loadedForm)}
          disabled={!dirty || mutation.isPending}
          className="shadow-lg"
        >
          Cancel
        </Button>
        <Button onClick={() => mutation.mutate()} disabled={!dirty || mutation.isPending} className="gap-2 shadow-lg">
          <Save className="h-4 w-4" aria-hidden />
          {mutation.isPending ? "Saving…" : "Save settings"}
        </Button>
      </div>
    </div>
  );
}
